import axios from "axios";

import getErrorDetails from "./helpers/getErrorDetails.js";
import paramsSerializer from "./helpers/paramsSerializer";
import {
  addPaginationAndSort,
  getPaginationInfoFromHeaders,
  ORDER_DESC,
  PaginatedList,
} from "./helpers/pagination";
import { UUID } from "../utils/uuid";

import User, { UserForm } from "../models/User";
import Role from "../models/Role.js";
import Customer from "../models/Customer.js";

export async function getUsers(options: any): Promise<PaginatedList<User>> {
  const params = addPaginationAndSort<User>({
    orderBy: "createdAt",
    order: ORDER_DESC,
    // TODO
    // remove after implementing pagination
    limit: 999,
    page: 0,
  });
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: "/users",
      params,
      paramsSerializer,
    });

    const { data = {}, headers: responseHeaders = {} } = response;

    const {
      totalItems,
      currentPage,
      currentLimit,
    } = getPaginationInfoFromHeaders(responseHeaders);

    return {
      list: data.map(unifyUser),
      totalItems,
      currentPage,
      currentLimit,
    };
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function getUser(id: UUID, options: any): Promise<User> {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/users/${id}`,
    });

    const { data = {} } = response;
    return unifyUser(data);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function postUser(user: UserForm, options: any): Promise<User> {
  try {
    const response = await axios({
      ...options,
      method: "POST",
      url: `/users`,
      data: user,
    });
    const { data = {} } = response;
    return await getUser(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function patchUser(
  id: UUID,
  user: UserForm,
  options: any
): Promise<User> {
  try {
    const response = await axios({
      ...options,
      method: "PUT",
      url: `/users/${id}`,
      data: user,
    });
    const { data = {} } = response;
    return await getUser(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function deleteUser(id: UUID, options: any) {
  try {
    await axios({
      ...options,
      method: "DELETE",
      url: `/users/${id}`,
    });
  } catch (error) {
    throw getErrorDetails(error);
  }
}

interface RawUser {
  id: UUID;
  name: string;
  email: string;
  roles: Role[];
  active: boolean;
  customer: Customer;
}

function unifyUser(user: RawUser): User {
  // Unify format to be in sync with other configuration endpoints
  return {
    ...user,
    customer: user?.customer?.id,
  };
}
